import { EntityType } from '@frontend/common';
import { Logger } from '@frontend/logger';
import { notifyEntityCreated } from '@frontend/repository';
import { SettingKey } from '@frontend/setting/types';
import { TransactionClient, TransactionExtendedClient } from '@frontend/transaction/api';
import { ExtendedTransaction, Transaction, TransactionStateName, TransactionType } from '@frontend/transaction/types';
import { MobileWizardStateRecord, updateData } from '@frontend/wizard/mobile';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import { useAppDispatch } from '../../redux/store';

/**
 * Transaction synchronization states:
 * - INIT: Transaction exists locally but not yet created on backend
 * - AHEAD: Local changes exist that haven't been synced to backend
 * - SYNCED: Local and backend are in sync
 */
export enum TransactionSyncState {
    INIT = 'init',
    AHEAD = 'ahead',
    SYNCED = 'synced'
}

export interface UseWizardTransactionOptions {
    wizardId?: string;
    store: MobileWizardStateRecord | null;

    /**
     * Auto-create transaction on mount if conditions are met
     * @default false
     */
    autoCreate?: boolean;

    /**
     * Auto-fetch transaction from URL params on mount
     * @default true
     */
    autoFetch?: boolean;

    /**
     * Callback fired when transaction is created
     */
    onCreate?: (transaction: ExtendedTransaction) => void | Promise<void>;

    /**
     * Additional query params to look for transaction ID
     * @default ['transaction_id']
     */
    transactionIdParams?: string[];
}

export interface UseWizardTransactionReturn {
    /** Current transaction data (merged local + backend) */
    transaction: Partial<ExtendedTransaction> | null;
    remoteTransaction: ExtendedTransaction | null;

    /** Loading state for async operations */
    isLoading: boolean;

    /** Current synchronization state */
    transactionState: TransactionSyncState;

    /**
     * Update transaction data locally (in Redux store)
     * Does not sync to backend immediately
     */
    localUpdateTransaction: (data: Partial<ExtendedTransaction>) => Promise<void>;

    /**
     * Create transaction on backend if in INIT state
     * Update transaction on backend if in AHEAD state
     * Transitions state from INIT|AHEAD to SYNCED
     */
    syncTransaction: (data?: Partial<ExtendedTransaction>) => Promise<ExtendedTransaction>;

    /**
     * Refresh transaction data from backend
     * Overwrites local changes
     */
    refresh: () => Promise<void>;

    /**
     * Clear cached remote transaction state held by this hook instance
     */
    clearRemoteTransaction: () => void;
}

/**
 * Hook for managing wizard transaction lifecycle and synchronization.
 * Platform-specific: used inside wizard modals (no location-state seeding).
 */
export const useWizardTransaction = (options: UseWizardTransactionOptions): UseWizardTransactionReturn => {
    const dispatch = useAppDispatch();
    const [searchParams] = useSearchParams();
    const { state: locationState } = useLocation();

    const [isLoading, setIsLoading] = useState(false);
    const [transactionState, setTransactionState] = useState<TransactionSyncState>(TransactionSyncState.INIT);
    const [remoteTransaction, setRemoteTransaction] = useState<ExtendedTransaction | null>(null);

    const syncPromiseRef = useRef<Promise<ExtendedTransaction> | null>(null);

    const { wizardId, store, autoCreate = false, autoFetch = true, transactionIdParams = ['transaction_id'] } = options;
    const localTransaction = store?.data?.transaction as ExtendedTransaction | undefined;
    const accountId = store?.wizard.account_id;
    const resolvedTransactionId = localTransaction?.id ?? transactionIdParams.map((param) => searchParams.get(param)).find((id) => id !== null) ?? undefined;
    const canCreateTransaction = store ? hasRequiredDataForCreation(store) : false;

    // Seed wizard store from React Router location state on mount
    useEffect(() => {
        if (wizardId && !localTransaction && locationState?.transaction) {
            dispatch(updateData({ key: wizardId, data: { transaction: locationState.transaction } }));
        }
    }, []);

    // Store ref to access latest store state without stale closures
    const storeRef = useRef(store);
    useEffect(() => {
        storeRef.current = store;
    }, [store]);

    const waitForStoreUpdate = async (checkFn: () => boolean, maxAttempts = 50, delayMs = 10) => {
        for (let i = 0; i < maxAttempts; i++) {
            if (checkFn()) return true;
            await new Promise((resolve) => setTimeout(resolve, delayMs));
        }
        return false;
    };

    /**
     * Initialize transaction from URL params or wizard store
     */
    useEffect(() => {
        if (!autoFetch || !accountId || !wizardId) return;

        const initializeTransaction = async () => {
            const currentStore = storeRef.current;
            if (!currentStore) return;

            const currentLocalTransaction = currentStore.data?.transaction as ExtendedTransaction | undefined;

            if (resolvedTransactionId) {
                setIsLoading(true);
                try {
                    const fetchedTransaction = await TransactionExtendedClient.fetchExtendedTransaction(accountId, resolvedTransactionId);
                    setRemoteTransaction(fetchedTransaction);

                    if (fetchedTransaction && !currentLocalTransaction) {
                        dispatch(updateData({ key: wizardId, data: { transaction: fetchedTransaction } }));
                        setTransactionState(TransactionSyncState.SYNCED);
                    } else {
                        let hasLocalChanges = false;
                        if (currentLocalTransaction) {
                            for (const key in currentLocalTransaction) {
                                if (Object.prototype.hasOwnProperty.call(currentLocalTransaction, key)) {
                                    const localValue = currentLocalTransaction[key as keyof ExtendedTransaction];
                                    const remoteValue = fetchedTransaction[key as keyof ExtendedTransaction];
                                    if (JSON.stringify(localValue) !== JSON.stringify(remoteValue)) {
                                        hasLocalChanges = true;
                                        break;
                                    }
                                }
                            }
                        }
                        setTransactionState(hasLocalChanges ? TransactionSyncState.AHEAD : TransactionSyncState.SYNCED);
                    }
                } catch (error) {
                    Logger.error('Failed to fetch transaction:', error);
                } finally {
                    setIsLoading(false);
                }
            } else {
                setTransactionState(TransactionSyncState.INIT);
            }
        };

        initializeTransaction();
    }, [accountId, wizardId, autoFetch, resolvedTransactionId, dispatch]);

    /**
     * Auto-create transaction if enabled and conditions are met
     */
    useEffect(() => {
        if (!autoCreate || !store || transactionState !== TransactionSyncState.INIT || syncPromiseRef.current) return;

        if (resolvedTransactionId) return;

        if (canCreateTransaction) {
            syncTransaction();
        }
    }, [autoCreate, store, transactionState, resolvedTransactionId, canCreateTransaction]);

    const localUpdateTransaction = async (data: Partial<ExtendedTransaction>, synced?: boolean) => {
        if (!store || !wizardId) {
            Logger.warn('Cannot update transaction: wizard store not available');
            return;
        }

        const newTransactionData = { ...(localTransaction || {}), ...data };

        await dispatch(updateData({ key: wizardId, data: { transaction: newTransactionData } }));

        if (transactionState === TransactionSyncState.SYNCED && synced !== true) {
            setTransactionState(TransactionSyncState.AHEAD);
        }

        await waitForStoreUpdate(() => {
            const currentStore = storeRef.current;
            const currentTransaction = currentStore?.data?.transaction as ExtendedTransaction | undefined;
            if (data.id && currentTransaction?.id !== data.id) return false;
            if (data.spot_id && currentTransaction?.spot_id !== data.spot_id) return false;
            if (data.slot_id && currentTransaction?.slot_id !== data.slot_id) return false;
            return true;
        });
    };

    const syncTransaction = async (data?: Partial<ExtendedTransaction>): Promise<ExtendedTransaction> => {
        if (!store) {
            throw new Error('Cannot create or update transaction: wizard store not available');
        }

        const existingTransactionId = remoteTransaction?.id || localTransaction?.id;
        const shouldCreate = !existingTransactionId;

        if (shouldCreate && syncPromiseRef.current) {
            const existing = await syncPromiseRef.current;
            if (data && Object.keys(data).length > 0) {
                return syncTransaction(data);
            }
            return existing;
        }

        const execute = async (): Promise<ExtendedTransaction> => {
            if (!existingTransactionId) {
                if (!hasRequiredDataForCreation(store)) {
                    throw new Error('Cannot create transaction: missing required data (workflow_id, functionality)');
                }
                setIsLoading(true);
                try {
                    const wfIdSetting = store.functionalitySettings.find((s) => s.key === SettingKey.TRANSACTION_WORKFLOW_ID);

                    const newTransaction = await TransactionClient.postTransaction(store.wizard.account_id, {
                        application: store.functionality.application,
                        feature: store.functionality.feature,
                        state: TransactionStateName.TRANSACTION_DRAFT,
                        workflow_id: wfIdSetting!.value as string,
                        type: TransactionType.PUDO,
                        ...localTransaction,
                        ...data
                    });
                    notifyEntityCreated(dispatch, EntityType.TRANSACTION);

                    const transaction = await TransactionExtendedClient.fetchExtendedTransaction(store.wizard.account_id, newTransaction.id);
                    await localUpdateTransaction(transaction, true);
                    setRemoteTransaction(transaction);
                    setTransactionState(TransactionSyncState.SYNCED);

                    if (options.onCreate) {
                        await options.onCreate(transaction);
                    }

                    return transaction;
                } catch (error) {
                    Logger.error('Failed to create transaction:', error);
                    throw error;
                } finally {
                    setIsLoading(false);
                }
            } else {
                let remote = remoteTransaction;
                const accountId = remoteTransaction?.account_id || localTransaction?.account_id || store.wizard.account_id;

                if (!remote) {
                    remote = await TransactionExtendedClient.fetchExtendedTransaction(accountId, existingTransactionId);
                    setRemoteTransaction(remote);
                }

                setIsLoading(true);
                try {
                    const updatedTransaction = await TransactionClient.patchTransaction(accountId, existingTransactionId, {
                        ...localTransaction,
                        ...data
                    });

                    const extendedTransaction = await TransactionExtendedClient.fetchExtendedTransaction(accountId, updatedTransaction.id);

                    await localUpdateTransaction(extendedTransaction, true);
                    setRemoteTransaction(extendedTransaction);
                    setTransactionState(TransactionSyncState.SYNCED);

                    return extendedTransaction;
                } catch (error) {
                    Logger.error('Failed to update transaction:', error);
                    throw error;
                } finally {
                    setIsLoading(false);
                }
            }
        };

        const promise = execute();
        if (shouldCreate) {
            syncPromiseRef.current = promise;
            promise.finally(() => {
                syncPromiseRef.current = null;
            });
        }

        return promise;
    };

    const refresh = async (): Promise<void> => {
        const currentLocalTransaction = storeRef.current?.data?.transaction as ExtendedTransaction | undefined;
        const transactionId = currentLocalTransaction?.id || remoteTransaction?.id || localTransaction?.id;
        if (!transactionId) throw new Error('Cannot refresh: no transaction ID');

        const accountId = currentLocalTransaction?.account_id || remoteTransaction?.account_id || localTransaction?.account_id;
        if (!accountId) throw new Error('Cannot refresh: no account ID');

        setIsLoading(true);
        try {
            const refreshedTransaction = await TransactionExtendedClient.fetchExtendedTransaction(accountId, transactionId);
            if (refreshedTransaction) {
                await localUpdateTransaction(refreshedTransaction, true);
                setRemoteTransaction(refreshedTransaction);
                setTransactionState(TransactionSyncState.SYNCED);
            }
        } catch (error) {
            Logger.error('Failed to refresh transaction:', error);
            throw error;
        } finally {
            setIsLoading(false);
        }
    };

    const clearRemoteTransaction = () => {
        setRemoteTransaction(null);
        setTransactionState(TransactionSyncState.INIT);
    };

    return {
        transaction: localTransaction ?? null,
        remoteTransaction,
        isLoading,
        transactionState,
        localUpdateTransaction,
        syncTransaction,
        refresh,
        clearRemoteTransaction
    };
};

export function hasRequiredDataForCreation(store: MobileWizardStateRecord): boolean {
    const wfIdSetting = store.functionalitySettings.find((s) => s.key === SettingKey.TRANSACTION_WORKFLOW_ID);
    return !!(wfIdSetting?.value && typeof wfIdSetting.value === 'string' && store.functionality?.application && store.functionality?.feature);
}

export function transactionToTransactionExtended(transaction: Transaction): ExtendedTransaction {
    return {
        ...transaction,
        senders: null,
        receivers: null,
        spot: null,
        slot: null,
        pin_codes: null
    };
}
